import type { JsonValue } from "@sherlog/capabilities";
import { z } from "zod";

export const CIRCUIT_WEBHOOK_MAX_FILTER_RULES = 20;
export const CIRCUIT_WEBHOOK_MAX_FILTER_FIELD_LENGTH = 256;
export const CIRCUIT_WEBHOOK_MAX_FILTER_VALUES = 50;
export const CIRCUIT_WEBHOOK_MAX_FILTER_VALUE_LENGTH = 1024;

export const circuitWebhookFilterSchema = z
  .array(
    z.strictObject({
      field: z
        .string()
        .min(1)
        .max(CIRCUIT_WEBHOOK_MAX_FILTER_FIELD_LENGTH)
        .refine((field) => {
          const parts = field.split(".");
          return parts.length <= 10 && parts.every((part) => part.trim() === part && part !== "");
        }, "Field must be a key or dot-path with at most 10 non-empty, unpadded segments"),
      values: z
        .array(
          z
            .string()
            .min(1)
            .max(CIRCUIT_WEBHOOK_MAX_FILTER_VALUE_LENGTH)
            .refine((value) => value.trim().length > 0, "Filter values must not be blank"),
        )
        .min(1)
        .max(CIRCUIT_WEBHOOK_MAX_FILTER_VALUES),
    }),
  )
  .max(CIRCUIT_WEBHOOK_MAX_FILTER_RULES);

export function matchesCircuitWebhookFilter(
  payload: JsonValue,
  filter?: z.infer<typeof circuitWebhookFilterSchema>,
): boolean {
  return (filter ?? []).every(({ field, values }) => {
    let value = payload;
    for (const part of field.split(".")) {
      if (
        value === null ||
        typeof value !== "object" ||
        !Object.prototype.hasOwnProperty.call(value, part)
      )
        return false;
      value = (value as Record<string, JsonValue>)[part]!;
    }
    // Strings compare literally; other JSON values use their JSON representation.
    return values.includes(typeof value === "string" ? value : JSON.stringify(value));
  });
}
